/**
 * Ventana de un combo o receta (spec 20): crear, editar (reemplaza la definición y sube la versión), duplicar,
 * desactivar y activar. Solo con «Descuentos programados» (`productos.descuentos`, por plantilla la dueña); los demás la
 * ven sin poder cambiarla. «Probar» calcula con una medida: el combo guardado lo calcula el servidor (igual que el punto
 * de venta); lo que todavía no se guardó, la misma función `calcularCombo` en pantalla.
 */
import {
  duplicarComboInput,
  formatearCantidad,
  Money,
  REDONDEO_COMPONENTE_ETIQUETA,
  REDONDEOS_COMPONENTE,
  UNIDAD_MEDIDA_ETIQUETA,
  UNIDADES_MEDIDA_COMBO,
  type CalculoComboDTO,
  type ComboDTO,
  type ModoComponente,
  type ProductoDetalleDTO,
  type ProductoResumenDTO,
  type RedondeoComponente,
  type TipoCombo,
  type UnidadMedidaCombo,
} from '@mg/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Copy, Layers, Plus, Replace, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { Cargando, ErrorEstado } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { cn } from '@/lib/utils.ts';
import { clavesProductos } from '@/modulos/productos/api.ts';
import { calcularCombo, useActivarCombo, useCombo, useDuplicarCombo, useGuardarCombo } from './api.ts';
import { DialogElegirProducto } from './DialogElegirProducto.tsx';
import {
  aDecimal,
  cambiarTipo,
  completarProducto,
  empaqueNuevo,
  erroresSueltos,
  etiquetaFactor,
  etiquetaModo,
  filaNueva,
  formularioDesdeCombo,
  formularioVacio,
  hayCambios,
  modosDelTipo,
  moverComponente,
  presentacionPorOmision,
  probarLocal,
  productoElegido,
  productosDelCombo,
  quitarComponente,
  validarFormulario,
  type FilaComponente,
  type FilaEmpaque,
  type FormularioCombo,
  type ProductoElegido,
} from './formulario.ts';
import { VistaPreviaCombo } from './VistaPreviaCombo.tsx';

/** Cómo se lee el tipo en el selector. */
const TIPO_OPCION: Record<TipoCombo, string> = { fijo: 'Combo de cantidades fijas', por_medida: 'Receta por medida' };

interface Props {
  abierto: boolean;
  /** null = combo nuevo. */
  comboId: string | null;
  cerrar: () => void;
  /** Abre otro combo en esta misma ventana (la copia recién duplicada). */
  abrir: (id: string) => void;
}

export function EditorCombo({ abierto, comboId, cerrar, abrir }: Props) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-6xl" data-testid="dialogo-combo">
        {abierto ? comboId ? <CargarCombo key={comboId} id={comboId} cerrar={cerrar} abrir={abrir} /> : <Editor combo={null} cerrar={cerrar} abrir={abrir} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function CargarCombo({ id, cerrar, abrir }: { id: string; cerrar: () => void; abrir: (id: string) => void }) {
  const combo = useCombo(id);
  if (combo.data) return <Editor combo={combo.data} cerrar={cerrar} abrir={abrir} />;
  return (
    <>
      <DialogHeader>
        <DialogTitle>Combo o receta</DialogTitle>
        <DialogDescription>{combo.error ? 'No se pudo abrir el combo.' : 'Cargando el combo…'}</DialogDescription>
      </DialogHeader>
      {combo.error ? <ErrorEstado error={combo.error} reintentar={() => void combo.refetch()} /> : <Cargando filas={6} />}
    </>
  );
}

/** Para qué se abrió el buscador de productos. */
type Elegir = { para: 'agregar' } | { para: 'cambiar'; clave: string; n: number } | { para: 'empaque'; clave: string };

function Editor({ combo, cerrar, abrir }: { combo: ComboDTO | null; cerrar: () => void; abrir: (id: string) => void }) {
  const { puede, usuario } = useAuth();
  // Un combo vale en todas las tiendas: lo configura la dueña (con el permiso de descuentos, que por plantilla es suyo)
  const editable = puede('productos.descuentos') && usuario?.rol === 'superadmin';
  const qc = useQueryClient();
  const guardar = useGuardarCombo();
  const activar = useActivarCombo();
  const [inicial] = useState(() => (combo ? formularioDesdeCombo(combo) : formularioVacio()));
  const [f, setF] = useState(inicial);
  // Los errores se muestran desde el primer «Guardar» y se corrigen en vivo
  const [intentado, setIntentado] = useState(false);
  const [elegir, setElegir] = useState<Elegir | null>(null);
  const [conEmpaques, setConEmpaques] = useState<Set<string>>(() => new Set(inicial.componentes.filter((k) => k.empaques.length > 0).map((k) => k.clave)));
  const [duplicando, setDuplicando] = useState(false);

  const validacion = useMemo(() => validarFormulario(f), [f]);
  const errores = intentado && !validacion.ok ? validacion.errores : {};
  const cambios = hayCambios(f, inicial);

  const setFila = (clave: string, cambio: Partial<FilaComponente>) => setF((x) => ({ ...x, componentes: x.componentes.map((k) => (k.clave === clave ? { ...k, ...cambio } : k)) }));
  const setEmpaques = (clave: string, cambiar: (es: FilaEmpaque[]) => FilaEmpaque[]) => setF((x) => ({ ...x, componentes: x.componentes.map((k) => (k.clave === clave ? { ...k, empaques: cambiar(k.empaques) } : k)) }));

  const excluir = useMemo(() => {
    const s = productosDelCombo(f);
    if (elegir?.para === 'cambiar') {
      const k = f.componentes.find((x) => x.clave === elegir.clave);
      if (k) s.delete(k.producto.producto_id);
    }
    return s;
  }, [f, elegir]);

  const alElegir = (p: ProductoResumenDTO) => {
    const elegido = productoElegido(p);
    if (elegir?.para === 'agregar') setF((x) => ({ ...x, componentes: [...x.componentes, filaNueva(elegido, x.tipo)] }));
    else if (elegir?.para === 'cambiar') setFila(elegir.clave, { producto: elegido, presentacion_id: presentacionPorOmision(elegido) });
    else if (elegir?.para === 'empaque') {
      const clave = elegir.clave;
      setEmpaques(clave, (es) => [...es, empaqueNuevo(elegido)]);
      setConEmpaques((s) => new Set(s).add(clave));
    }
    setElegir(null);
  };

  /** Un producto de un combo guardado trae solo la presentación elegida: las demás llegan al abrir su lista. */
  const pedirPresentaciones = (p: ProductoElegido) => {
    if (p.completo) return;
    qc.fetchQuery({ queryKey: clavesProductos.detalle(p.producto_id), queryFn: () => get<ProductoDetalleDTO>(`/productos/${p.producto_id}`) }).then(
      (detalle) => setF((x) => completarProducto(x, detalle)),
      () => undefined,
    );
  };

  const enviar = () => {
    setIntentado(true);
    if (!validacion.ok) {
      toast.error('Revisa el combo', { description: Object.values(validacion.errores)[0] });
      return;
    }
    guardar.mutate(
      { id: combo?.id ?? null, datos: validacion.datos },
      {
        onSuccess: (c) => {
          toast.success(combo ? `Combo ${c.codigo} guardado` : `Combo ${c.codigo} creado`, { description: `${c.nombre} · ${c.componentes.length} producto(s)${combo ? ` · versión ${c.version}` : ''}` });
          cerrar();
        },
        onError: (e) => toast.error('No se pudo guardar el combo', { description: mensajeDeError(e) }),
      },
    );
  };

  const tituloElegir =
    elegir?.para === 'cambiar'
      ? { titulo: `Cambiar el producto ${elegir.n}`, descripcion: 'Se conserva cómo sale la cantidad. Revisa después la presentación y el contenido.' }
      : elegir?.para === 'empaque'
        ? { titulo: 'Agregar un empaque alternativo', descripcion: 'Otro empaque del mismo material, por ejemplo el rollo de 76 m de la cinta. Después escribe lo que trae.' }
        : { titulo: 'Agregar producto al combo', descripcion: 'Busca el producto y elígelo. Entra con la presentación de una unidad; después puedes cambiarla.' };

  return (
    <div className="space-y-4" data-testid="editor-combo">
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <Layers className="h-5 w-5 text-mg-verde" /> {combo ? `${combo.codigo} · ${combo.nombre}` : 'Nuevo combo o receta'} <AyudaVentana tema="combos.editar" />
          {combo && !combo.activo ? <Badge variant="neutro">Desactivado</Badge> : null}
          {combo && !combo.vendible ? <Badge variant="critico">Producto desactivado</Badge> : null}
          {combo ? <Badge variant="neutro">versión {combo.version}</Badge> : null}
        </DialogTitle>
        <DialogDescription>
          {editable
            ? 'Sus productos, cómo sale la cantidad de cada uno y el descuento. El descuento del combo solo vale cuando se vende completo.'
            : 'Solo la dueña cambia los combos. Aquí ves cómo está armado y puedes probarlo con una medida.'}
        </DialogDescription>
      </DialogHeader>

      <fieldset disabled={!editable} className="min-w-0 space-y-4">
        <div className="grid grid-cols-6 gap-3">
          <Campo etiqueta="Código" htmlFor="combo-codigo" error={errores.codigo}>
            <Input id="combo-codigo" value={f.codigo} onChange={(e) => setF((x) => ({ ...x, codigo: e.target.value }))} placeholder="TECHO-DW" className="uppercase" aria-invalid={!!errores.codigo} data-testid="combo-codigo" />
          </Campo>
          <Campo etiqueta="Nombre" htmlFor="combo-nombre" error={errores.nombre} className="col-span-2">
            <Input id="combo-nombre" value={f.nombre} onChange={(e) => setF((x) => ({ ...x, nombre: e.target.value }))} placeholder="Techo en dry wall" aria-invalid={!!errores.nombre} data-testid="combo-nombre" />
          </Campo>
          <Campo etiqueta="Tipo" htmlFor="combo-tipo">
            <Select id="combo-tipo" value={f.tipo} onChange={(e) => setF((x) => cambiarTipo(x, e.target.value as TipoCombo))} data-testid="combo-tipo">
              {(Object.keys(TIPO_OPCION) as TipoCombo[]).map((t) => (
                <option key={t} value={t}>{TIPO_OPCION[t]}</option>
              ))}
            </Select>
          </Campo>
          {f.tipo === 'por_medida' ? (
            <Campo etiqueta="Unidad de medida" htmlFor="combo-unidad" error={errores.unidad_medida}>
              <Select id="combo-unidad" value={f.unidad_medida} onChange={(e) => setF((x) => ({ ...x, unidad_medida: e.target.value as UnidadMedidaCombo }))} aria-invalid={!!errores.unidad_medida} data-testid="combo-unidad">
                {UNIDADES_MEDIDA_COMBO.map((u) => (
                  <option key={u} value={u}>{UNIDAD_MEDIDA_ETIQUETA[u]}</option>
                ))}
              </Select>
            </Campo>
          ) : (
            <p className="self-end pb-2 text-xs text-muted-foreground">Se venden 1, 2, 3… combos</p>
          )}
          <Campo etiqueta="Descuento para todos %" htmlFor="combo-descuento" error={errores.descuento_pct} ayuda="Un producto puede traer el suyo">
            <Input id="combo-descuento" inputMode="decimal" value={f.descuento} onChange={(e) => setF((x) => ({ ...x, descuento: e.target.value }))} placeholder="0" className="text-right" aria-invalid={!!errores.descuento_pct} data-testid="combo-descuento" />
          </Campo>
        </div>
        <Campo etiqueta="Descripción" htmlFor="combo-descripcion" error={errores.descripcion}>
          <Textarea id="combo-descripcion" rows={2} value={f.descripcion} onChange={(e) => setF((x) => ({ ...x, descripcion: e.target.value }))} placeholder="Para qué trabajo es y qué incluye" className="min-h-0" data-testid="combo-descripcion" />
        </Campo>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              Productos del combo <span className="font-normal text-muted-foreground">({f.componentes.length})</span>
            </h3>
            {editable ? (
              <Button size="sm" variant="outline" onClick={() => setElegir({ para: 'agregar' })} data-testid="combo-agregar-producto">
                <Plus /> Agregar producto
              </Button>
            ) : null}
          </div>
          {errores.componentes ? <p className="text-xs text-destructive" role="alert">{errores.componentes}</p> : null}
          {f.componentes.length === 0 ? (
            <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="combo-sin-productos">
              Todavía no tiene productos. Pulsa «Agregar producto» y elige el primero.
            </div>
          ) : (
            f.componentes.map((k, i) => (
              <FilaProducto
                key={k.clave}
                k={k}
                i={i}
                f={f}
                errores={errores}
                editable={editable}
                verEmpaques={conEmpaques.has(k.clave)}
                alternarEmpaques={() =>
                  setConEmpaques((s) => {
                    const n = new Set(s);
                    if (n.has(k.clave)) n.delete(k.clave);
                    else n.add(k.clave);
                    return n;
                  })
                }
                cambiar={(cambio) => setFila(k.clave, cambio)}
                cambiarEmpaques={(fn) => setEmpaques(k.clave, fn)}
                mover={(paso) => setF((x) => moverComponente(x, i, paso))}
                quitar={() => setF((x) => quitarComponente(x, k.clave))}
                elegirProducto={() => setElegir({ para: 'cambiar', clave: k.clave, n: i + 1 })}
                elegirEmpaque={() => setElegir({ para: 'empaque', clave: k.clave })}
                pedirPresentaciones={pedirPresentaciones}
              />
            ))
          )}
        </div>
      </fieldset>

      <Probar combo={combo} f={f} cambios={cambios} />

      {combo && editable ? <p className="text-xs text-muted-foreground">Guardar cambios no toca lo ya vendido ni cotizado. Un combo que esté en un carrito sin cobrar hay que volver a agregarlo.</p> : null}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <div className="flex items-center gap-2">
          {combo && editable ? (
            <>
              <BotonActivo
                activo={combo.activo}
                titulo={`Desactivar el combo «${combo.nombre}»`}
                consecuencia="No se puede agregar al POS ni a cotizaciones hasta activarlo. Lo ya vendido no cambia."
                variante="outline"
                disabled={activar.isPending}
                alCambiar={(activo) =>
                  activar.mutateAsync({ id: combo.id, activo }).then(
                    () => {
                      toast.success(activo ? `Combo ${combo.codigo} activado` : `Combo ${combo.codigo} desactivado`);
                    },
                    (e) => {
                      toast.error(mensajeDeError(e));
                    },
                  )
                }
                testid="combo-alternar"
              />
              <Button size="sm" variant="outline" onClick={() => setDuplicando(true)} data-testid="combo-duplicar">
                <Copy /> Duplicar
              </Button>
            </>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cerrar} data-testid="combo-cancelar">
            {editable ? 'Cancelar' : 'Cerrar'}
          </Button>
          {editable ? (
            <Button onClick={enviar} cargando={guardar.isPending} data-testid="combo-guardar">
              {combo ? 'Guardar cambios' : 'Guardar combo'}
            </Button>
          ) : null}
        </div>
      </div>

      <DialogElegirProducto abierto={!!elegir} titulo={tituloElegir.titulo} descripcion={tituloElegir.descripcion} cerrar={() => setElegir(null)} alElegir={alElegir} excluir={excluir} />
      {combo ? <DialogDuplicar combo={combo} cambios={cambios} abierto={duplicando} cerrar={() => setDuplicando(false)} alDuplicar={abrir} /> : null}
    </div>
  );
}

// ─── Una fila: un producto del combo ─────────────────────────────────────────

/** Rótulo pequeño arriba del control y su error (o su ayuda) debajo: las filas de productos van apretadas. */
function CampoFila({ etiqueta, error, ayuda, className, children }: { etiqueta: string; error?: string | undefined; ayuda?: string | undefined; className?: string; children: React.ReactNode }) {
  return (
    <label className={cn('grid content-start gap-0.5 text-[11px] font-medium text-muted-foreground', className)}>
      {etiqueta}
      {children}
      {error ? (
        <span className="font-normal text-destructive" role="alert">
          {error}
        </span>
      ) : ayuda ? (
        <span className="font-normal">{ayuda}</span>
      ) : null}
    </label>
  );
}

function ayudaFactor(modo: ModoComponente, tipo: TipoCombo): string {
  if (modo === 'fijo') return tipo === 'fijo' ? 'Cuántos lleva cada combo' : 'Va una sola vez por trabajo';
  if (modo === 'consumo') return 'En piezas, kg o metros (lámina 3/8: 0,36)';
  return 'Lámina de 1,22 × 0,61 = 0,72';
}

/** «Caja x12 · 12 unidades»: la presentación con lo que trae si no es la unidad. */
function textoPresentacion(p: { nombre: string; unidades: string }): string {
  const u = Money.parse(p.unidades);
  return u.eq('1') ? p.nombre : `${p.nombre} · ${formatearCantidad(u, 4)} unidades`;
}

interface PropsFila {
  k: FilaComponente;
  i: number;
  f: FormularioCombo;
  errores: Record<string, string>;
  editable: boolean;
  verEmpaques: boolean;
  alternarEmpaques: () => void;
  cambiar: (cambio: Partial<FilaComponente>) => void;
  cambiarEmpaques: (fn: (es: FilaEmpaque[]) => FilaEmpaque[]) => void;
  mover: (paso: -1 | 1) => void;
  quitar: () => void;
  elegirProducto: () => void;
  elegirEmpaque: () => void;
  pedirPresentaciones: (p: ProductoElegido) => void;
}

function FilaProducto({ k, i, f, errores, editable, verEmpaques, alternarEmpaques, cambiar, cambiarEmpaques, mover, quitar, elegirProducto, elegirEmpaque, pedirPresentaciones }: PropsFila) {
  const ruta = `componentes.${i}`;
  const err = (campo: string) => errores[`${ruta}.${campo}`];
  const sueltos = erroresSueltos(errores, ruta, ['modo', 'factor', 'contenido', 'base_orden', 'ajuste_pct', 'descuento_pct', ...k.empaques.map((_, j) => `empaques.${j}.contenido`)]);
  const bases = f.componentes.filter((x) => x.clave !== k.clave && x.modo !== 'derivado');
  const posicion = (clave: string) => f.componentes.findIndex((x) => x.clave === clave) + 1;
  const modos = modosDelTipo(f.tipo);
  const descuentoCombo = aDecimal(f.descuento);

  const cambiarModo = (modo: ModoComponente) => {
    // Al pasar a derivado se propone el primer producto que no es derivado (las láminas, casi siempre)
    if (modo === 'derivado' && !k.base_clave) cambiar({ modo, base_clave: bases[0]?.clave ?? null });
    else cambiar({ modo });
  };

  return (
    <div className={cn('rounded-xl border bg-card p-3', sueltos.length > 0 && 'border-destructive/60')} data-testid={`combo-componente-${i + 1}`}>
      <div className="flex flex-wrap items-start gap-2">
        <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mg-verde-claro text-xs font-semibold text-mg-verde-oscuro num">{i + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">
            {k.producto.nombre} {!k.producto.activo ? <Badge variant="critico">Producto desactivado</Badge> : null}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">{k.producto.codigo}</p>
        </div>
        <CampoFila etiqueta="Presentación" error={err('presentacion_id')} className="w-56">
          <Select
            value={k.presentacion_id}
            onFocus={() => pedirPresentaciones(k.producto)}
            onMouseDown={() => pedirPresentaciones(k.producto)}
            onChange={(e) => cambiar({ presentacion_id: e.target.value })}
            aria-label={`Presentación de ${k.producto.nombre}`}
            className="h-8 py-1"
            data-testid={`combo-presentacion-${i + 1}`}
          >
            {k.producto.presentaciones.map((p) => (
              <option key={p.id} value={p.id}>{textoPresentacion(p)}</option>
            ))}
          </Select>
        </CampoFila>
        {editable ? (
          <div className="flex items-center gap-0.5 pt-4">
            <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => mover(-1)} aria-label={`Subir ${k.producto.nombre}`} title="Subir">
              <ArrowUp />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === f.componentes.length - 1} onClick={() => mover(1)} aria-label={`Bajar ${k.producto.nombre}`} title="Bajar">
              <ArrowDown />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={elegirProducto} aria-label={`Cambiar ${k.producto.nombre} por otro producto`} title="Cambiar por otro producto" data-testid={`combo-cambiar-${i + 1}`}>
              <Replace />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-mg-rojo" onClick={quitar} aria-label={`Quitar ${k.producto.nombre} del combo`} title="Quitar del combo" data-testid={`combo-quitar-${i + 1}`}>
              <Trash2 />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex flex-wrap items-start gap-2 pl-8">
        <CampoFila etiqueta="Cómo sale la cantidad" error={err('modo')} className="w-60">
          <Select value={k.modo} onChange={(e) => cambiarModo(e.target.value as ModoComponente)} className="h-8 py-1" aria-label={`Cómo sale la cantidad de ${k.producto.nombre}`} data-testid={`combo-modo-${i + 1}`}>
            {(modos.includes(k.modo) ? modos : [...modos, k.modo]).map((m) => (
              <option key={m} value={m}>{etiquetaModo(m, f.unidad_medida)}</option>
            ))}
          </Select>
        </CampoFila>
        {k.modo === 'derivado' ? (
          <>
            <CampoFila etiqueta="Sale del producto" error={err('base_orden')} className="w-60">
              <Select value={k.base_clave ?? ''} onChange={(e) => cambiar({ base_clave: e.target.value || null })} className="h-8 py-1" aria-label={`De qué producto sale ${k.producto.nombre}`} data-testid={`combo-base-${i + 1}`}>
                <option value="">Elige de cuál sale…</option>
                {bases.map((b) => (
                  <option key={b.clave} value={b.clave}>{posicion(b.clave)}. {b.producto.nombre}</option>
                ))}
              </Select>
            </CampoFila>
            <CampoFila etiqueta="Ajuste %" error={err('ajuste_pct')} ayuda="−10 = 10 % menos" className="w-28">
              <Input inputMode="decimal" value={k.ajuste} onChange={(e) => cambiar({ ajuste: e.target.value })} placeholder="0" className="h-8 text-right" data-testid={`combo-ajuste-${i + 1}`} />
            </CampoFila>
          </>
        ) : (
          <CampoFila etiqueta={etiquetaFactor(k.modo, f.tipo, f.unidad_medida)} error={err('factor')} ayuda={ayudaFactor(k.modo, f.tipo)} className="w-48">
            <Input inputMode="decimal" value={k.factor} onChange={(e) => cambiar({ factor: e.target.value })} className="h-8 text-right" data-testid={`combo-factor-${i + 1}`} />
          </CampoFila>
        )}
        <CampoFila etiqueta="Contenido de la unidad que se vende" error={err('contenido')} ayuda="Caja de 100 tornillos = 100; cuñete de 28 kg = 28" className="w-56">
          <Input inputMode="decimal" value={k.contenido} onChange={(e) => cambiar({ contenido: e.target.value })} placeholder="1" className="h-8 text-right" data-testid={`combo-contenido-${i + 1}`} />
        </CampoFila>
        <CampoFila etiqueta="Redondeo" className="w-56">
          <Select value={k.redondeo} onChange={(e) => cambiar({ redondeo: e.target.value as RedondeoComponente })} className="h-8 py-1" aria-label={`Redondeo de ${k.producto.nombre}`} data-testid={`combo-redondeo-${i + 1}`}>
            {REDONDEOS_COMPONENTE.map((r) => (
              <option key={r} value={r}>{REDONDEO_COMPONENTE_ETIQUETA[r]}</option>
            ))}
          </Select>
        </CampoFila>
        <CampoFila etiqueta="Desc. propio %" error={err('descuento_pct')} ayuda="Vacío: el del combo" className="w-28">
          <Input inputMode="decimal" value={k.descuento} onChange={(e) => cambiar({ descuento: e.target.value })} placeholder={descuentoCombo || '0'} className="h-8 text-right" data-testid={`combo-desc-${i + 1}`} />
        </CampoFila>
      </div>

      {editable || k.empaques.length > 0 ? (
        <div className="mt-2 pl-8">
          {editable ? (
            <button type="button" className="text-xs font-medium text-mg-verde hover:underline" onClick={alternarEmpaques} aria-expanded={verEmpaques} data-testid={`combo-empaques-${i + 1}`}>
              Empaques alternativos ({k.empaques.length}) · {verEmpaques ? 'ocultar' : 'ver'}
            </button>
          ) : (
            <p className="text-xs font-medium text-muted-foreground">Empaques alternativos ({k.empaques.length})</p>
          )}
          {verEmpaques || !editable ? (
            <div className="mt-1 space-y-1.5 rounded-lg bg-muted/40 p-2">
              <p className="text-[11px] text-muted-foreground">Otro empaque del mismo material para completar sin pasarse (el rollo de 76 m junto al de 150 m). Lo que trae va en la misma unidad que el consumo.</p>
              {k.empaques.map((e, j) => (
                <div key={e.clave} className="flex flex-wrap items-start gap-2" data-testid={`combo-empaque-${i + 1}-${j + 1}`}>
                  <div className="min-w-0 flex-1 pt-1.5 text-sm">
                    <span className="font-medium">{e.producto.nombre}</span> <span className="font-mono text-[11px] text-muted-foreground">{e.producto.codigo}</span>
                  </div>
                  <Select
                    value={e.presentacion_id}
                    onFocus={() => pedirPresentaciones(e.producto)}
                    onMouseDown={() => pedirPresentaciones(e.producto)}
                    onChange={(ev) => cambiarEmpaques((es) => es.map((x) => (x.clave === e.clave ? { ...x, presentacion_id: ev.target.value } : x)))}
                    className="h-8 w-48 py-1"
                    aria-label={`Presentación de ${e.producto.nombre}`}
                  >
                    {e.producto.presentaciones.map((p) => (
                      <option key={p.id} value={p.id}>{textoPresentacion(p)}</option>
                    ))}
                  </Select>
                  <div className="grid gap-0.5">
                    <Input
                      inputMode="decimal"
                      value={e.contenido}
                      onChange={(ev) => cambiarEmpaques((es) => es.map((x) => (x.clave === e.clave ? { ...x, contenido: ev.target.value } : x)))}
                      placeholder="Trae (76)"
                      className="h-8 w-28 text-right"
                      aria-label={`Lo que trae ${e.producto.nombre}`}
                      aria-invalid={!!err(`empaques.${j}.contenido`)}
                      data-testid={`combo-empaque-contenido-${i + 1}-${j + 1}`}
                    />
                    {err(`empaques.${j}.contenido`) ? (
                      <span className="max-w-56 text-[11px] text-destructive" role="alert">
                        {err(`empaques.${j}.contenido`)}
                      </span>
                    ) : null}
                  </div>
                  {editable ? (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-mg-rojo" onClick={() => cambiarEmpaques((es) => es.filter((x) => x.clave !== e.clave))} aria-label={`Quitar el empaque ${e.producto.nombre}`} title="Quitar el empaque">
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>
              ))}
              {editable && k.empaques.length < 5 ? (
                <Button size="sm" variant="ghost" onClick={elegirEmpaque} data-testid={`combo-agregar-empaque-${i + 1}`}>
                  <Plus /> Agregar empaque
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {sueltos.map((m) => (
        <p key={m} className="mt-1 pl-8 text-xs text-destructive" role="alert">
          {m}
        </p>
      ))}
    </div>
  );
}

// ─── Probar con una medida ───────────────────────────────────────────────────

function Probar({ combo, f, cambios }: { combo: ComboDTO | null; f: FormularioCombo; cambios: boolean }) {
  const [medida, setMedida] = useState('');
  const delServidor = !!combo && !cambios;
  const local = useMemo(() => (delServidor ? null : probarLocal(f, medida)), [delServidor, f, medida]);
  const medidaNormal = aDecimal(medida);
  const comboId = combo?.id ?? null;
  const medidaValida = /^\d{1,12}(\.\d{1,8})?$/.test(medidaNormal) && Money.parse(medidaNormal).esPositivo();
  // Qué se le pide al servidor: el combo, su versión y la medida. Una respuesta de otra clave ya no se muestra.
  const clave = delServidor && comboId && medidaValida ? `${comboId}|${combo?.version ?? 0}|${medidaNormal}` : null;
  const [servidor, setServidor] = useState<{ clave: string; calculo: CalculoComboDTO | null; error: string | null } | null>(null);

  // El combo guardado lo calcula el servidor (debounce corto), igual que en el punto de venta
  useEffect(() => {
    if (!clave || !comboId) return;
    let vivo = true;
    const t = setTimeout(() => {
      calcularCombo(comboId, medidaNormal, false).then(
        (c) => vivo && setServidor({ clave, calculo: c, error: null }),
        (e) => vivo && setServidor({ clave, calculo: null, error: mensajeDeError(e) }),
      );
    }, 200);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [clave, comboId, medidaNormal]);

  const delMismo = servidor && servidor.clave === clave ? servidor : null;
  const renglones = delServidor ? (delMismo?.calculo?.renglones ?? null) : local && 'renglones' in local ? local.renglones : null;
  const error = delServidor ? (delMismo?.error ?? null) : local && 'error' in local ? local.error : null;
  const etiqueta = f.tipo === 'fijo' ? '¿Cuántos combos?' : `Medida del trabajo (${UNIDAD_MEDIDA_ETIQUETA[f.unidad_medida || 'm2']})`;

  return (
    <div className="space-y-2 rounded-xl border bg-muted/40 p-3" data-testid="combo-probar">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Probar</p>
          <p className="text-xs text-muted-foreground">
            {delServidor ? 'Calcula con el combo guardado, igual que el punto de venta.' : combo ? 'Calcula con lo que está en pantalla: los cambios todavía no se guardan.' : 'Calcula con lo que está en pantalla, antes de guardar.'}
          </p>
        </div>
        <Campo etiqueta={etiqueta} htmlFor="combo-probar-medida">
          <Input id="combo-probar-medida" inputMode="decimal" value={medida} onChange={(e) => setMedida(e.target.value)} placeholder={f.tipo === 'fijo' ? '1' : '26'} className="w-40 text-right" data-testid="combo-probar-medida" />
        </Campo>
      </div>
      {error ? (
        <p className="rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-800" data-testid="combo-probar-error">
          {error}
        </p>
      ) : null}
      {renglones ? <VistaPreviaCombo renglones={renglones} /> : null}
    </div>
  );
}

// ─── Duplicar (una receta por color) ─────────────────────────────────────────

function DialogDuplicar({ combo, cambios, abierto, cerrar, alDuplicar }: { combo: ComboDTO; cambios: boolean; abierto: boolean; cerrar: () => void; alDuplicar: (id: string) => void }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-md" data-testid="dialogo-duplicar-combo">
        <DialogHeader>
          <DialogTitle>Duplicar «{combo.nombre}»</DialogTitle>
          <DialogDescription>La copia lleva los mismos productos con otro código y otro nombre. Sirve para tener una receta por color: duplica y cambia los productos del color.</DialogDescription>
        </DialogHeader>
        {abierto ? <FormDuplicar combo={combo} cambios={cambios} cerrar={cerrar} alDuplicar={alDuplicar} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function FormDuplicar({ combo, cambios, cerrar, alDuplicar }: { combo: ComboDTO; cambios: boolean; cerrar: () => void; alDuplicar: (id: string) => void }) {
  const duplicar = useDuplicarCombo();
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState(`${combo.nombre} (copia)`);
  const [errores, setErrores] = useState<{ codigo?: string; nombre?: string }>({});
  const enviar = () => {
    const r = duplicarComboInput.safeParse({ codigo, nombre });
    if (!r.success) {
      const e: { codigo?: string; nombre?: string } = {};
      for (const i of r.error.issues) {
        const campo = i.path[0];
        if ((campo === 'codigo' || campo === 'nombre') && !e[campo]) e[campo] = i.message;
      }
      setErrores(e);
      return;
    }
    setErrores({});
    duplicar.mutate(
      { id: combo.id, ...r.data },
      {
        onSuccess: (c) => {
          toast.success(`Copia ${c.codigo} creada`, { description: `${c.nombre}: cámbiale los productos que hagan falta y guarda.` });
          alDuplicar(c.id);
        },
        onError: (e) => toast.error('No se pudo duplicar el combo', { description: mensajeDeError(e) }),
      },
    );
  };
  return (
    <div className="space-y-3">
      {cambios ? <p className="rounded-lg bg-amber-50 px-3 py-1.5 text-sm text-amber-800">Se copia el combo guardado. Los cambios que no guardaste no pasan a la copia.</p> : null}
      <Campo etiqueta="Código de la copia" htmlFor="dup-codigo" error={errores.codigo}>
        <Input id="dup-codigo" autoFocus value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder={`${combo.codigo}-BLANCO`} className="uppercase" aria-invalid={!!errores.codigo} data-testid="dup-codigo" />
      </Campo>
      <Campo etiqueta="Nombre de la copia" htmlFor="dup-nombre" error={errores.nombre}>
        <Input id="dup-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} aria-invalid={!!errores.nombre} data-testid="dup-nombre" />
      </Campo>
      <DialogFooter>
        <Button variant="outline" onClick={cerrar}>
          Cancelar
        </Button>
        <Button onClick={enviar} cargando={duplicar.isPending} data-testid="dup-confirmar">
          Duplicar
        </Button>
      </DialogFooter>
    </div>
  );
}
